import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from 'src/app/shared/permission.service';
import { BaridOfflineMode } from '../../barid/barid-contracts';
import { OFFLINE_STATE_DISABLED, OfflineStateService, OfflineUiState } from '../offline-state.service';

export interface OfflineIndicatorView {
  level: 'ok' | 'warn' | 'offline' | 'loading';
  icon: string;
  label: string;
}

const MODE_LABELS: Record<BaridOfflineMode, string> = {
  ONLINE: 'Online',
  DEGRADED: 'Conexão instável',
  OFFLINE: 'Offline',
  RESYNCING: 'Sincronizando'
};

/** Visual do indicador. `null` = nao mostrar (chave OFF, Barid ausente ou sem o offline adotado). */
export function offlineIndicatorView(state: OfflineUiState): OfflineIndicatorView | null {
  if (!state.enabled) return null;
  const pending = state.pending > 0 ? ` ${state.pending} operação(ões) aguardando envio.` : '';
  const held = state.held > 0 ? ` ${state.held} retida(s) para revisão.` : '';
  const tail = `${pending}${held}`;
  switch (state.mode) {
    case 'OFFLINE':
      return { level: 'offline', icon: 'cloud_off', label: `Modo offline: operando pelo Barid deste computador.${tail}` };
    case 'RESYNCING':
      return { level: 'loading', icon: 'sync', label: `Sincronizando com a nuvem.${tail}` };
    case 'DEGRADED':
      return { level: 'warn', icon: 'signal_cellular_alt_2_bar', label: `Conexão com a nuvem instável.${tail}` };
    default:
      return { level: 'ok', icon: 'cloud_done', label: `${MODE_LABELS.ONLINE}.${tail}` };
  }
}

/**
 * Indicador de conectividade do modo offline na barra superior. Reaproveita a role do indicador do Barid
 * (BARID_CONNECTIVITY_INDICATOR_SHOW): quem nao ve o Barid nao ve este. Nasce oculto: so aparece com a chave
 * OFFLINE_EDGE ligada e o Barid deste computador reportando `offline.enabled`.
 */
@Component({
  selector: 'app-offline-indicator',
  templateUrl: './offline-indicator.component.html',
  styleUrls: ['./offline-indicator.component.scss']
})
export class OfflineIndicatorComponent implements OnInit, OnDestroy {
  view: OfflineIndicatorView | null = null;
  state: OfflineUiState = OFFLINE_STATE_DISABLED;

  private sub?: Subscription;

  constructor(
    private readonly offline: OfflineStateService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    if (!this.permissionService.is(this.permissionService.systemActionRoles.BARID_CONNECTIVITY_INDICATOR_SHOW)) return;
    this.sub = this.offline.state$.subscribe((state) => {
      this.state = state;
      this.view = offlineIndicatorView(state);
    });
    this.offline.start();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.offline.stop();
  }
}
